/**
 * Employee Form Component
 * Form for creating/editing employees
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import logger from '../utils/logger';
import centerService from '../services/centerService';
import settingsService from '../services/settingsService';

const EmployeeForm = ({ employee, employees, centers = [], onSubmit, onCancel, isPasswordReset = false, restrictRole = null, hideManagerAndCenter = false, managerCenterId = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'employee',
    managerId: '',
    centerId: managerCenterId || '',
    yearlyTokens: 12,
    password: '',
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [effectiveTokenLimit, setEffectiveTokenLimit] = useState(12);
  const [tokenLimitSource, setTokenLimitSource] = useState('system');
  
  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee.name || '',
        email: employee.email || '',
        role: employee.role || 'employee',
        managerId: employee.managerId || '',
        centerId: employee.centerId || '',
        yearlyTokens: employee.yearlyTokens || 12,
        password: '', // Never pre-fill password
      });
    } else if (managerCenterId) {
      // For new employees created by managers, pre-populate with manager's center
      setFormData(prev => ({
        ...prev,
        centerId: managerCenterId,
      }));
    }
  }, [employee, managerCenterId]);
  
  // Fetch effective token limit when center changes or on initial load
  useEffect(() => {
    const fetchEffectiveLimit = async () => {
      try {
        if (formData.centerId) {
          // Center is selected, get its effective limit
          const limit = await centerService.getEffectiveYearlyTokenLimit(formData.centerId);
          const selectedCenter = centers.find(c => c.id === formData.centerId);
          
          setEffectiveTokenLimit(limit);
          
          if (selectedCenter && selectedCenter.yearlyTokenLimit !== null && selectedCenter.yearlyTokenLimit !== undefined) {
            setTokenLimitSource('center');
          } else {
            setTokenLimitSource('system-via-center');
          }
          
          // Auto-update yearlyTokens if not editing an existing employee
          if (!employee) {
            setFormData(prev => ({ ...prev, yearlyTokens: limit }));
          }
        } else {
          // No center selected, use system default
          const systemSettings = await settingsService.getSettings();
          const limit = systemSettings.yearlyTokenLimit || 12;
          
          setEffectiveTokenLimit(limit);
          setTokenLimitSource('system');
          
          // Auto-update yearlyTokens if not editing an existing employee
          if (!employee) {
            setFormData(prev => ({ ...prev, yearlyTokens: limit }));
          }
        }
      } catch (error) {
        logger.error('Error fetching effective token limit', error);
        setEffectiveTokenLimit(12);
        setTokenLimitSource('system');
      }
    };
    
    fetchEffectiveLimit();
  }, [formData.centerId, centers, employee]);
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    } else if (
      employees.some(
        emp => emp.email === formData.email && (!employee || emp.id !== employee.id)
      )
    ) {
      newErrors.email = 'Email already in use';
    }
    
    if (formData.yearlyTokens < 0) {
      newErrors.yearlyTokens = 'Yearly tokens must be non-negative';
    }
    
    // Password validation (required for new employees or password reset)
    if ((!employee || isPasswordReset) && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.trim() && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      logger.warning('Employee form validation failed', { errors, formData });
      return;
    }
    
    logger.info('Submitting employee form', { formData, isEdit: !!employee });
    
    // Convert empty strings to null for UUID fields
    const cleanedData = {
      ...formData,
      managerId: formData.managerId || null,
      centerId: formData.centerId || null,
    };
    
    onSubmit(cleanedData);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'yearlyTokens' ? parseInt(value) || 0 : value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // Get available managers (employees with manager or admin role)
  const availableManagers = employees.filter(
    emp => (emp.role === 'manager' || emp.role === 'admin') && emp.id !== employee?.id
  );
  
  logger.debug('EmployeeForm rendered', { isEdit: !!employee });
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white ${
            errors.name ? 'border-red-500' : ''
          }`}
          required
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>
      
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white ${
            errors.email ? 'border-red-500' : ''
          }`}
          required
        />
        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
      </div>
      
      {!restrictRole ? (
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
            Role *
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white"
            required
          >
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      ) : (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Role: <span className="font-semibold">Employee</span>
          </p>
          <p className="text-xs text-gray-500">Managers can only create employee accounts</p>
        </div>
      )}
      
      {formData.role === 'employee' && !hideManagerAndCenter && (
        <div>
          <label htmlFor="managerId" className="block text-sm font-medium text-gray-700 mb-1">
            Manager
          </label>
          <select
            id="managerId"
            name="managerId"
            value={formData.managerId}
            onChange={handleChange}
            className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white"
          >
            <option value="">No Manager</option>
            {availableManagers.map(manager => (
              <option key={manager.id} value={manager.id}>
                {manager.name}
              </option>
            ))}
          </select>
        </div>
      )}
      
      {!hideManagerAndCenter && (
        <div>
          <label htmlFor="centerId" className="block text-sm font-medium text-gray-700 mb-1">
            Center/Team
          </label>
          <select
            id="centerId"
            name="centerId"
            value={formData.centerId}
            onChange={handleChange}
            className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white"
          >
            <option value="">No Center</option>
            {centers.map(center => (
              <option key={center.id} value={center.id}>
                {center.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">Select the center/team this employee belongs to</p>
        </div>
      )}
      
      <div>
        <label htmlFor="yearlyTokens" className="block text-sm font-medium text-gray-700 mb-1">
          Yearly Tokens *
        </label>
        <input
          type="number"
          id="yearlyTokens"
          name="yearlyTokens"
          value={formData.yearlyTokens}
          onChange={handleChange}
          min="0"
          className={`w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white ${
            errors.yearlyTokens ? 'border-red-500' : ''
          }`}
          required
        />
        {errors.yearlyTokens && (
          <p className="mt-1 text-sm text-red-600">{errors.yearlyTokens}</p>
        )}
        {formData.centerId && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              {tokenLimitSource === 'center' && (
                <>
                  <span className="font-semibold">Center-specific limit:</span> {centers.find(c => c.id === formData.centerId)?.name || 'This center'} has a configured limit of {effectiveTokenLimit} tokens
                </>
              )}
              {tokenLimitSource === 'system-via-center' && (
                <>
                  <span className="font-semibold">Inherited from System:</span> {centers.find(c => c.id === formData.centerId)?.name || 'This center'} uses the system default of {effectiveTokenLimit} tokens
                </>
              )}
            </p>
          </div>
        )}
        {!formData.centerId && (
          <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600">
              <span className="font-semibold">System default:</span> {effectiveTokenLimit} tokens (no center assigned)
            </p>
          </div>
        )}
      </div>
      
      {/* Password field - only show for new employees or password reset */}
      {(!employee || isPasswordReset) && (
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password {isPasswordReset ? '(Reset)' : '*'}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-3 py-2 pr-10 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white ${
                errors.password ? 'border-red-500' : ''
              }`}
              placeholder={isPasswordReset ? 'Enter new password' : 'Enter password (min 6 characters)'}
              required={!employee || isPasswordReset}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password}</p>
          )}
          {!errors.password && formData.password && (
            <p className="mt-1 text-xs text-gray-500">
              Password must be at least 6 characters long
            </p>
          )}
        </div>
      )}
      
      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
        >
          {employee ? 'Update Employee' : 'Create Employee'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

EmployeeForm.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    centerId: PropTypes.string,
    role: PropTypes.string,
    managerId: PropTypes.string,
    yearlyTokens: PropTypes.number,
  }),
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
    })
  ).isRequired,
  centers: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  isPasswordReset: PropTypes.bool,
  restrictRole: PropTypes.bool,
  hideManagerAndCenter: PropTypes.bool,
  managerCenterId: PropTypes.string,
};

export default EmployeeForm;

